import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Search, Package, ShoppingCart, Users, Building2, Shield, Lock, LogOut, Trash2, RefreshCw,
  Activity, Zap, Key, Bell,
  UserCircle, BellDot, Sun, Moon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import Logo from '../Logo';
import logger from '../../utils/logger';
import toast from '../../utils/toast';
import OrganizationSwitcher from '../OrganizationSwitcher';
import api from '../../services/api';
import { Menu } from 'lucide-react';

const Topbar = ({ onToggleSidebar, mobileMenuOpen, onCloseMobileMenu }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [executionTime, setExecutionTime] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [_currentTime, setCurrentTime] = useState(new Date());
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const searchRef = useRef(null);
  const adminMenuRef = useRef(null);
  const debounceTimer = useRef(null);
  const [_error, setError] = useState(null);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close search results and admin menu when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
        setSelectedIndex(-1);
      }
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target)) {
        setShowAdminMenu(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowResults(false);
        setShowAdminMenu(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults, suggestions]);

  // Autocomplete suggestions (faster, 150ms debounce)
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    if (searchQuery.trim().length >= 1) {
      debounceTimer.current = setTimeout(() => {
        fetchAutocomplete();
      }, 150);
    } else {
      setSuggestions([]);
    }

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Full search with 300ms debounce
  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch();
      } else {
        setSearchResults(null);
      }
    }, 300);

    return () => clearTimeout(debounce);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchAutocomplete = async () => {
    try {
      const response = await api.get(`/search/autocomplete?q=${encodeURIComponent(searchQuery)}`);
      setSuggestions(response.data.suggestions || []);
      setError(null);
    } catch (error) {
      logger.warn('Search autocomplete failed', { error, query: searchQuery });
      setError(null); // Don't show error for autocomplete failures
    }
  };

  const performSearch = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/search?q=${encodeURIComponent(searchQuery)}&limit=5`);
      setSearchResults(response.data);
      setExecutionTime(response.data.executionTime || 0);
      setShowResults(true);
      setSuggestions([]); // Clear suggestions when showing full results
      setError(null);
    } catch (error) {
      logger.apiError('/search', error);
      setError(null); // Don't break UI on search errors
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, _id) => {
    const routes = {
      products: `/inventory/products`,
      orders: `/orders`,
      suppliers: `/suppliers`,
      warehouses: `/warehouses`
    };
    navigate(routes[type]);
    setShowResults(false);
    setSearchQuery('');
    setSelectedIndex(-1);
  };

  const handleSuggestionClick = (suggestionText) => {
    setSearchQuery(suggestionText);
    setSelectedIndex(-1);
    // This will trigger the search useEffect
  };

  // Get all clickable items (suggestions or results)
  const getAllItems = () => {
    if (!showResults && suggestions.length > 0) {
      return suggestions;
    }
    if (showResults && searchResults) {
      return [
        ...(searchResults.products || []).map(p => ({ ...p, type: 'products' })),
        ...(searchResults.orders || []).map(o => ({ ...o, type: 'orders' })),
        ...(searchResults.suppliers || []).map(s => ({ ...s, type: 'suppliers' })),
        ...(searchResults.warehouses || []).map(w => ({ ...w, type: 'warehouses' }))
      ];
    }
    return [];
  };

  const handleKeyDown = (e) => {
    const items = getAllItems();
    if (items.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < items.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < items.length) {
          const item = items[selectedIndex];
          if (!showResults && item.text) {
            // Autocomplete suggestion
            handleSuggestionClick(item.text);
          } else if (item.type) {
            // Full search result
            handleResultClick(item.type, item._id);
          }
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const getIcon = (type) => {
    const icons = {
      products: Package,
      orders: ShoppingCart,
      suppliers: Users,
      warehouses: Building2
    };
    const Icon = icons[type];
    return <Icon className="w-4 h-4" />;
  };

  const handleAdminAction = async (action) => {
    setShowAdminMenu(false);

    switch(action) {
      case 'account':
        navigate('/settings/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'preferences':
        navigate('/settings/profile');
        break;
      case 'security':
        navigate('/settings/profile');
        break;
      case 'notifications':
        navigate('/notifications');
        break;
      case 'users':
        navigate('/admin/users');
        break;
      case 'roles':
        navigate('/admin/roles');
        break;
      case 'permissions':
        navigate('/admin/permissions');
        break;
      case 'audit':
        navigate('/admin/audit-trail');
        break;
      case 'logs':
        navigate('/admin/logs');
        break;
      case 'amazon':
        navigate('/integrations/amazon');
        break;
      case 'shopify':
        navigate('/integrations/shopify');
        break;
      case 'notion':
        navigate('/integrations/notion');
        break;
      case 'tokens':
        navigate('/admin/api-tokens');
        break;
      case 'clear-cache':
        if (!confirm('Clear all cached data? This may temporarily slow performance during rebuild.')) {
          return;
        }
        try {
          toast.info('Clearing cache...');
          await api.post('/analytics/cache/clear');
          toast.success('Cache cleared successfully');
        } catch (err) {
          logger.apiError('/analytics/cache/clear', err);
          toast.error('Failed to clear cache');
        }
        break;
      case 'sync-data':
        navigate('/admin/sync');
        break;
      case 'jobs':
        navigate('/admin/background-jobs');
        break;
      case 'health':
        navigate('/admin/health');
        break;
      case 'lock':
        // Lock session - redirect to lock screen
        navigate('/lock');
        break;
      case 'logout':
        logout();
        navigate('/login');
        break;
      default:
        break;
    }
  };

  return (
    <header className="sticky top-0 z-50 h-16 bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-dark-700 px-4 md:px-6">
      <div className="h-full flex items-center justify-between gap-4 md:gap-6">

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => {
            setShowAdminMenu(false);
            onToggleSidebar();
          }}
          className="md:hidden p-2 -ml-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
          aria-label="Toggle navigation menu"
        >
          <Menu size={24} />
        </button>

        {/* LEFT ZONE: Logo */}
        <Link to="/dashboard" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity group">
          <Logo size="sm" variant="mini" className="group-hover:scale-105 transition-transform" />
          {/* Brand Text */}
          <div className="leading-tight hidden sm:block">
            <div className="text-sm font-semibold tracking-wide text-brand-600 dark:text-brand-400">
              GABHAN ERP
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              MANAGEMENT SYSTEM
            </div>
          </div>
        </Link>

        {/* CENTER ZONE: Global Search */}
        <div className="flex-1 max-w-2xl" ref={searchRef}>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
              <Search className="text-gray-400" size={18} />
            </div>

            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchResults && setShowResults(true)}
              onKeyDown={handleKeyDown}
              placeholder="Search products, orders, suppliers..."
              className="w-full h-10 pl-11 pr-10 bg-gray-50 dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-full focus:outline-none focus:ring-2 focus:ring-[#2563EB]/50 focus:border-[#2563EB] transition-all text-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
            />

            {loading && (
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-gray-300 dark:border-dark-600 border-t-[#2563EB] rounded-full animate-spin"></div>
              </div>
            )}

            {!loading && searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setShowResults(false);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-200 dark:hover:bg-dark-600 rounded-full transition-colors"
              >
                <span className="text-gray-400 text-sm">✕</span>
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showResults && searchResults && searchResults.total > 0 && (
            <div className="absolute top-full mt-2 w-full bg-white dark:bg-dark-800 rounded-lg shadow-xl dark:shadow-dark-soft-xl border border-gray-200 dark:border-dark-700 max-h-96 overflow-y-auto z-50">
              {/* Products */}
              {searchResults.products?.length > 0 && (
                <div className="p-2">
                  <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Products</div>
                  {searchResults.products.map((item, idx) => {
                    const globalIdx = idx;
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleResultClick('products', item._id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-300 dark:border-brand-700' : 'hover:bg-gray-50 dark:hover:bg-dark-700'
                        }`}
                      >
                        {getIcon('products')}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{item.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">${item.price} • Stock: {item.stock}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Orders */}
              {searchResults.orders?.length > 0 && (
                <div className="p-2 border-t border-border">
                  <div className="text-xs font-semibold text-muted-foreground px-3 py-2">Orders</div>
                  {searchResults.orders.map((item, idx) => {
                    const globalIdx = (searchResults.products?.length || 0) + idx;
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleResultClick('orders', item._id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-300 dark:border-brand-700' : 'hover:bg-muted'
                        }`}
                      >
                        {getIcon('orders')}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">
                            {item.user?.name || item.user?.email || 'Order'} #{item._id?.slice(-6)}
                          </div>
                          <div className="text-xs text-muted-foreground">${item.total} • {item.status}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Suppliers */}
              {searchResults.suppliers?.length > 0 && (
                <div className="p-2 border-t border-border">
                  <div className="text-xs font-semibold text-muted-foreground px-3 py-2">Suppliers</div>
                  {searchResults.suppliers.map((item, idx) => {
                    const globalIdx = (searchResults.products?.length || 0) + (searchResults.orders?.length || 0) + idx;
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleResultClick('suppliers', item._id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-300 dark:border-brand-700' : 'hover:bg-muted'
                        }`}
                      >
                        {getIcon('suppliers')}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.email}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Warehouses */}
              {searchResults.warehouses?.length > 0 && (
                <div className="p-2 border-t border-border">
                  <div className="text-xs font-semibold text-muted-foreground px-3 py-2">Warehouses</div>
                  {Array.isArray(searchResults.warehouses) && searchResults.warehouses.map((item, idx) => {
                    const globalIdx = (searchResults.products?.length || 0) + (searchResults.orders?.length || 0) + (searchResults.suppliers?.length || 0) + idx;
                    return (
                      <button
                        key={item._id}
                        onClick={() => handleResultClick('warehouses', item._id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                          selectedIndex === globalIdx ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-300 dark:border-brand-700' : 'hover:bg-muted'
                        }`}
                      >
                        {getIcon('warehouses')}
                        <div className="flex-1">
                          <div className="text-sm font-medium text-foreground">{item.name}</div>
                          <div className="text-xs text-muted-foreground">{item.location?.city}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="p-3 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{searchResults.total} results found</span>
                  <span className="text-muted-foreground/70 flex items-center gap-1">
                    <span className="text-green-600">⚡</span>
                    {executionTime}ms
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Autocomplete Suggestions (when no full results yet) */}
          {!showResults && suggestions.length > 0 && searchQuery.length >= 1 && (
            <div className="absolute top-full mt-2 w-full bg-card rounded-lg shadow-xl border border-border max-h-64 overflow-y-auto z-50">
              <div className="p-2">
                <div className="text-xs font-semibold text-muted-foreground px-3 py-2">Suggestions</div>
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion.text)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                      selectedIndex === idx ? 'bg-brand-50 dark:bg-brand-900/20 border border-brand-300 dark:border-brand-700' : 'hover:bg-muted'
                    }`}
                  >
                    <Search className="w-3 h-3 text-muted-foreground" />
                    <span className="text-sm text-foreground">{suggestion.text}</span>
                    <span className="ml-auto text-xs text-muted-foreground capitalize">{suggestion.type}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No Results */}
          {showResults && searchResults && searchResults.total === 0 && searchQuery.length >= 2 && (
            <div className="absolute top-full mt-2 w-full bg-card rounded-lg shadow-xl border border-border p-4 z-50">
              <div className="text-center text-muted-foreground text-sm">No results found for &quot;{searchQuery}&quot;</div>
            </div>
          )}
        </div>

        {/* RIGHT ZONE: Theme + Notifications + Organization + Admin */}
        <div className="flex items-center gap-3">
        {/* Theme Toggle Icon */}
        <button
          onClick={toggleTheme}
          className="p-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors"
          aria-label={`Current theme: ${theme}. Click to toggle`}
          title={`Current: ${theme}`}
        >
          {theme === 'light' ? (
            <Sun size={20} className="text-yellow-500" />
          ) : theme === 'dark' ? (
            <Moon size={20} className="text-brand-400" />
          ) : (
            <Activity size={20} className="text-purple-500" />
          )}
        </button>

        {/* Organization Switcher */}
        <OrganizationSwitcher />

        {/* Notifications Icon with brand badge */}
        <button
          onClick={() => navigate('/notifications')}
          className="relative p-2.5 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-xl transition-all group"
          aria-label="Notifications"
        >
          <Bell size={20} className="text-gray-600 dark:text-gray-400 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors" />
          <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-brand-500 rounded-full ring-2 ring-white dark:ring-gray-900"></span>
        </button>

        {/* Admin Dropdown with brand avatar ring */}
        <div className="relative" ref={adminMenuRef}>
          <button
            onClick={() => {
              const willOpen = !showAdminMenu;
              if (willOpen && mobileMenuOpen) {
                onCloseMobileMenu?.();
              }
              setShowAdminMenu(willOpen);
            }}
            className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-100 dark:hover:bg-dark-800 rounded-xl transition-all"
          >
            {/* Avatar with brand ring */}
            <div className="relative">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-9 h-9 rounded-full object-cover shadow-md"
                />
              ) : (
                <div className="w-9 h-9 bg-gradient-to-br from-brand-500 to-brand-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md">
                  {user?.name?.charAt(0).toUpperCase() || 'U'}
                </div>
              )}
              <div className="absolute inset-0 w-9 h-9 rounded-full ring-2 ring-brand-400 dark:ring-brand-600 ring-offset-2 ring-offset-white dark:ring-offset-gray-900"></div>
            </div>
            <div className="text-left hidden lg:block">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {user?.name || 'User'}
              </div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {typeof user?.role === 'object' ? user?.role?.name : user?.role || 'Admin'}
              </div>
            </div>
          </button>

          {/* Dropdown Menu */}
          {showAdminMenu && (
            <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-dark-800 rounded-xl shadow-2xl border border-gray-200 dark:border-dark-700 py-3 z-50 animate-slideDown">
              {/* User Info Header */}
              <div className="px-4 py-3 border-b border-gray-100 dark:border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center text-white font-semibold text-sm shadow-md">
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 dark:text-white truncate">{user?.name || 'User'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
                  </div>
                </div>
              </div>

              {/* ACCOUNT Section */}
              <div className="px-3 pt-3 pb-1">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Account</p>
              </div>
              <button onClick={() => handleAdminAction('account')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <UserCircle size={18} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Profile Settings</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Manage your account</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('notifications')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <BellDot size={18} className="text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Notifications</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Alerts & preferences</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('security')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Shield size={18} className="text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Security</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Password & 2FA</div>
                  </div>
                </div>
              </button>

              <div className="border-t border-gray-100 dark:border-dark-700 my-2"></div>

              {/* SYSTEM Section - Admin Only */}
              {(user?.role?.name === 'admin' || user?.role === 'admin' || user?.isAdmin) && (
                <>
              <div className="px-3 pt-1 pb-1">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">System</p>
              </div>
              <button onClick={() => handleAdminAction('tokens')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Key size={18} className="text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">API Keys</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Developer credentials</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('jobs')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Zap size={18} className="text-cyan-600 dark:text-cyan-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Background Jobs</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Task queue status</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('sync-data')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-700/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <RefreshCw size={18} className="text-brand-500 dark:text-brand-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Data Sync</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Refresh integrations</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('clear-cache')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Trash2 size={18} className="text-orange-600 dark:text-orange-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Clear Cache</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Reset cached data</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('health')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Activity size={18} className="text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">System Health</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Performance metrics</div>
                  </div>
                </div>
              </button>
                </>
              )}

              <div className="border-t border-gray-100 dark:border-dark-700 my-2"></div>

              {/* SESSION Section */}
              <div className="px-3 pt-1 pb-1">
                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Session</p>
              </div>
              <button onClick={() => handleAdminAction('lock')} className="w-full px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-dark-700 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-dark-700 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Lock size={18} className="text-gray-600 dark:text-gray-300" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">Lock Screen</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">Secure your session</div>
                  </div>
                </div>
              </button>
              <button onClick={() => handleAdminAction('logout')} className="w-full px-4 py-2.5 text-left hover:bg-red-50 dark:hover:bg-red-900/20 transition-all group">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <LogOut size={18} className="text-red-600 dark:text-red-400" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-600 dark:text-red-400">Sign Out</div>
                    <div className="text-xs text-red-500 dark:text-red-400/70">End your session</div>
                  </div>
                </div>
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
